import { Component, ElementRef, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core'
import { NgTemplateOutlet } from '@angular/common'
import { Router } from '@angular/router'

import { BottomSheetComponent } from '../bottom-sheet/bottom-sheet.component'

import { InboxItem, InboxService } from '../../services/inbox.service'
import { ViewportService } from '../../services/viewport.service'
import { holdOverlayWhile } from '../../services/open-overlays.service'
import { WhatsNewItem, WhatsNewService } from '../../whats-new/whats-new.service'
import { WhatsNewDemoComponent, hasWhatsNewDemo } from '../../whats-new/whats-new-demo.component'

/**
 * The top bar's bell (Release B2, CC 11/10/2026): an unread count, and the latest messages
 * in a panel -- a dropdown on a laptop, a full-width sheet under the bar on a phone. A message
 * opens what it is about and is marked read. What's new (CC 18/10/2026) is the second tab: every
 * feature announcement of the last 30 days for this person's role, each opening its card.
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [WhatsNewDemoComponent, BottomSheetComponent, NgTemplateOutlet],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private inbox = inject(InboxService)
  private router = inject(Router)
  private host = inject(ElementRef<HTMLElement>)
  private whatsNew = inject(WhatsNewService)

  readonly items = this.inbox.items
  readonly unread = this.inbox.unread
  readonly news = this.whatsNew.items
  readonly unseen = this.whatsNew.unseen
  protected readonly hasDemo = hasWhatsNewDemo
  readonly open = signal(false)
  /** In the phone layout the inbox is a bottom sheet (Release 5, 31/10/2026), on the app's one
   *  phone rule, so a sideways phone gets the sheet too (Release 7, 04/11/2026, SHELL2-05). */
  readonly isPhone = inject(ViewportService).isPhone
  /** A pick made in the phone sheet: carried out once the sheet's history entry is gone, so the
   *  sheet's step back cannot undo the navigation (see BottomSheetComponent). */
  private afterSheet: (() => void) | null = null
  readonly tab = signal<'inbox' | 'new'>('inbox')
  /** Both kinds count: unread messages and features not looked at yet. */
  readonly total = computed(() => this.unread() + this.unseen())
  readonly badge = computed(() => (this.total() > 9 ? '9+' : String(this.total())))
  readonly label = computed(() => {
    const parts: string[] = []
    if (this.unread()) parts.push(this.unread() + ' unread')
    if (this.unseen()) parts.push(this.unseen() + ' new ' + (this.unseen() === 1 ? 'feature' : 'features'))
    return parts.length ? 'Notifications, ' + parts.join(', ') : 'Notifications'
  })

  constructor () {
    // The laptop dropdown can reach the downloads bar's corner: it steps aside while the inbox is
    // open (Release 7, 04/11/2026, SHELL2-01). The phone sheet holds it too; holds are counted.
    holdOverlayWhile(() => this.open())
  }

  ngOnInit (): void {
    this.inbox.start()
    void this.whatsNew.refreshList()
  }

  ngOnDestroy (): void {
    this.inbox.stop()
  }

  toggle (): void {
    this.open.set(!this.open())
    if (this.open()) {
      // Straight to What's new when that is the only thing waiting.
      this.tab.set(!this.unread() && this.unseen() ? 'new' : 'inbox')
      void this.inbox.refresh()
      void this.whatsNew.refreshList()
    }
  }

  pickNew (item: WhatsNewItem): void {
    this.closeThen(() => this.whatsNew.openFromBell(item))
  }

  /** Close, then act: straight away on a laptop, after the sheet has settled on a phone. */
  private closeThen (action: () => void): void {
    if (this.isPhone() && this.open()) {
      this.afterSheet = action
      this.open.set(false)
      return
    }
    this.open.set(false)
    action()
  }

  /** The sheet's ✕, backdrop, Escape or the phone's Back. */
  onSheetOpenChange (open: boolean): void {
    if (!open) this.open.set(false)
  }

  onSheetClosed (): void {
    const action = this.afterSheet
    this.afterSheet = null
    action?.()
  }

  day (iso: string): string {
    const d = new Date(iso)
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
  }

  pick (item: InboxItem): void {
    void this.inbox.markRead(item.id)
    this.closeThen(() => { if (item.link) void this.router.navigateByUrl(item.link) })
  }

  markAll (): void {
    void this.inbox.markRead()
  }

  when (iso: string): string {
    const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
    if (minutes < 1) return 'just now'
    // Full words (Release 5, 31/10/2026: the no-abbreviations rule).
    if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    const d = new Date(iso)
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
  }

  // The laptop dropdown closes on a click elsewhere and on Escape; the phone sheet has its own
  // backdrop, ✕ and Escape, and closing it from here would skip its history step.
  @HostListener('document:click', ['$event'])
  onDocumentClick (event: MouseEvent): void {
    if (this.isPhone()) return
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) this.open.set(false)
  }

  @HostListener('document:keydown.escape')
  onEscape (): void {
    if (!this.isPhone()) this.open.set(false)
  }

}
